import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminCrudPage, AdminCrudPageConfig } from '@/components/admin';
import api from '@/services/api';
import { InsurerEmployeeCreateModal } from './InsurerEmployeeCreateModal';

/**
 * Board #203. Creating an insurer employee now uses the standard Add User
 * form (see `InsurerEmployeeCreateModal`) instead of asking for an insurer
 * plus an EXISTING user id -- which required the account to have been created
 * somewhere else first, with the right role, before it could be mapped here.
 *
 * Editing an existing row keeps the generic modal: `employee_code`, `role`
 * and `designation` live on the mapping, not on the account, so an edit is an
 * ordinary PATCH of `/insurers/employees/{id}/`. `user` and `insurer` are not
 * editable -- moving an employee to a different account or a different
 * insurer is a create, not an edit.
 *
 * Board #282. This page lists staff across ALL insurers the admin is
 * accountable for (`InsurerEmployeeViewSet.get_queryset` scopes a country admin
 * by insurer country), so it needs a way to narrow to one. `FilterConfig` takes
 * static options only, so the config is built inside the component from the
 * insurer list rather than declared at module scope.
 *
 * `hasImportExport` is deliberately absent: `InsurerEmployeeViewSet` does not
 * mix in `core.import_export.ImportExportMixin`, so its `export/` segment
 * resolves to the router's DETAIL pattern and 404s (#49, same defect).
 */
const baseConfig: AdminCrudPageConfig<{ id: string; [key: string]: unknown }> = {
  title: 'Insurer Employees',
  apiEndpoint: '/insurers/employees/',
  queryKey: 'insurerEmployees',
  entityName: 'Insurer Employee',
  columns: [
    { key: 'user_name', label: 'Name' },
    { key: 'user_email', label: 'Email' },
    { key: 'user_phone', label: 'Phone' },
    { key: 'insurer_name', label: 'Insurer' },
    { key: 'employee_code', label: 'Employee Code' },
    { key: 'role_display', label: 'Role' },
    { key: 'designation', label: 'Designation' },
    { key: 'is_active', label: 'Status', render: (v: unknown) => (v ? 'Active' : 'Inactive') },
  ],
  formFields: [
    { name: 'employee_code', label: 'Employee Code', type: 'text' },
    { name: 'role', label: 'Role', type: 'select', options: [
      { value: 'admin', label: 'Admin' },
      { value: 'rm', label: 'Relationship Manager' },
      { value: 'underwriter', label: 'Underwriter' },
      { value: 'claims', label: 'Claims Handler' },
      { value: 'support', label: 'Support' },
    ], required: true },
    { name: 'designation', label: 'Designation', type: 'text' },
    { name: 'is_active', label: 'Active', type: 'boolean' },
  ],
  searchPlaceholder: 'Search employees...',
  // Removing an insurer employee now REVOKES their access -- the account is
  // suspended and its insurer association cleared, in the same transaction as
  // the mapping delete. Previously the row vanished and the login kept working,
  // so an admin who deleted someone believed access was gone when it was not.
  // Say so before the click, not after.
  deleteWarning: (
    <>
      <strong>This also revokes their login.</strong> The account is suspended and its
      link to this insurer is removed, so the person can no longer sign in to the insurer
      portal. The account itself is not deleted -- a super or country admin can reinstate it
      from Users. If this login is also used elsewhere on the platform (a broker, agent or
      customer record), only the insurer access is removed and the login stays active.
    </>
  ),
  hasActivate: true,
  renderCreateForm: ({ onClose, onCreated }) => (
    <InsurerEmployeeCreateModal onClose={onClose} onCreated={onCreated} />
  ),
};

interface InsurerOption {
  id: string | number;
  name?: string;
}

export function InsurerEmployeesPage() {
  // 100 is this API's max_page_size — same reasoning as InsurerEmployeeCreateModal:
  // a filter that silently offers the first 20 of 30 insurers reads as "we don't
  // have that insurer".
  const { data } = useQuery({
    queryKey: ['fk-options', '/insurers/insurers/?page_size=100'],
    queryFn: async () => {
      const response = await api.get('/insurers/insurers/?page_size=100');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const config = useMemo(() => {
    const rows: InsurerOption[] = Array.isArray(data) ? data : (data?.results ?? []);
    return {
      ...baseConfig,
      // `insurer` is in InsurerEmployeeViewSet.filterset_fields, so the value
      // reaches the queryset as ?insurer=<id>.
      filters: [
        {
          key: 'insurer',
          label: 'All insurers',
          options: rows.map((row) => ({
            value: String(row.id),
            label: String(row.name ?? row.id),
          })),
        },
      ],
    };
  }, [data]);

  return <AdminCrudPage config={config} />;
}
